import { useEffect, useRef, useState } from 'react';
import { speechErrorMessage } from '../../utils';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

/**
 * Dictation into a text box: every final phrase is handed to onText. The same
 * recognition settings the property and vendor notes windows use.
 */
export default function useSpeechInput(onText) {
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);
    const onTextRef = useRef(onText);

    onTextRef.current = onText;

    const stop = () => {
        if (recognitionRef.current) recognitionRef.current.stop();
        recognitionRef.current = null;
        setListening(false);
    };

    const start = () => {
        if (!SpeechRecognition) {
            alert('Speech recognition is not supported in this browser.');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'en-US';
        recognition.interimResults = false;
        recognition.continuous = true;
        recognitionRef.current = recognition;

        recognition.onresult = (event) => {
            let transcript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) transcript += event.results[i][0].transcript;
            }

            if (transcript) onTextRef.current(transcript);
        };
        // Without this the button flicks on and off and nothing says why.
        recognition.onerror = (event) => {
            const reason = event && event.error;

            recognitionRef.current = null;
            setListening(false);

            if (reason !== 'no-speech' && reason !== 'aborted') alert(speechErrorMessage(reason));
        };
        recognition.onend = () => {
            recognitionRef.current = null;
            setListening(false);
        };

        try {
            recognition.start();
            setListening(true);
        } catch (e) {
            // start() throws while an earlier recognition is still running.
            recognitionRef.current = null;
            setListening(false);
        }
    };

    useEffect(() => () => {
        if (recognitionRef.current) recognitionRef.current.stop();
    }, []);

    return { listening, toggle: () => (listening ? stop() : start()), stop };
}
